import { useCallback, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router";

import { useTranslation } from "@coworker/locales";
import {
  useStoreId,
  useTeamId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import useRefillAreaName from "../../hooks/useRefillAreaName";
import useArticlesByRefillArea from "../../hooks/useItemsByRefillArea";
import {
  addonTemplate,
  formatAddonsToCreate,
  formatAddonsToCreateV2,
  getStateAndAssignment,
} from "../../helpers/multipleAddonsHelper";

import FullScreenPopup from "../../../layout/components/FullScreenPopup/FullScreenPopup";
import NewAssign, {
  Assignee,
} from "@coworker/app/src/components/InputPopup/NewAssign";
import { ItemsContext } from "../../context/itemsContext";
import selectedItemsReducer from "../../context/selectedItemsReducer";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import ActionBarContent from "./ActionBarContent";
import { ItemsViewLinkbar } from "./ItemsViewLinkbar";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { useCreateTasksMutation } from "../../../tasks/hooks/useCreateTasksMutation";
import { useCreateTasksBatch } from "../../hooks/useCreateTasksBatch";
import { useMultiOrderAddonsFilters } from "../../hooks/useMultiOrderAddonsFilters";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import tasksSettingsReducer from "../../context/tasksSettingsReducer";
import { TasksSettingsContext } from "../../context/tasksSettingsContext";
import styled from "styled-components";
import FixaButton from "../../../shared/wrappers/FixaButton/FixaButton";
import EllipsesVerticalPath from "../../../shared/wrappers/FixaSSRIcon/paths/EllipsesVerticalPath";
import TasksSettingsModal from "./TasksSettings/TasksSettingsModal";
import ItemsListView from "@coworker/apprestructured/src/multi-order-addons/components/ItemsSelection/ItemsListView/ItemsListView";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";

const FloatingOptionsButton = styled(FixaButton)`
  position: absolute;
  bottom: 112px;
  left: 16px;
`;

const ItemsSelectionView = () => {
  const [selectedItems, dispatch] = useReducer(selectedItemsReducer, {});
  const [tasksSettings, tasksSettingsDispatch] = useReducer(
    tasksSettingsReducer,
    {
      markAsCompleted: false,
      markAsPriority: false,
    }
  );
  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const createTasksMutation = useCreateTasksMutation();
  const createTasksBatch = useCreateTasksBatch();

  const enableAddonOrdersV2 = useFlag(FLAGS.ENABLE_ADDON_ORDERS_V2);
  const { data: taskTypes } = useAllTaskTypes();
  const addonOrderTaskTypeId = useMemo(
    () => taskTypes?.find(({ name }) => name === TaskTypeNames.ADDON_ORDER)?.id,
    [taskTypes]
  );

  const multiOrderAddonsFilters = useMultiOrderAddonsFilters();

  const { t } = useTranslation();
  const { showToast } = useToastNotification();
  const uid = useUID();
  const storeId = useStoreId();
  const teamId = useTeamId();

  const { refillAreaId } = useParams();
  const refillAreaName = useRefillAreaName(refillAreaId || "");

  const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useArticlesByRefillArea(
      refillAreaId || "",
      multiOrderAddonsFilters.filterValues
    );

  const items = useMemo(() => {
    return data?.pages?.map(({ items }) => items).flat();
  }, [data?.pages]);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignee, setAssignee] = useState({
    gid: tasksSettings.markAsCompleted ? teamId : "",
    uid: tasksSettings.markAsCompleted ? uid : "",
  });

  const memoizedAddonTemplate = useMemo(
    () => ({
      ...addonTemplate,
      creator_id: uid,
      last_editor_id: uid,
      store_id: storeId,
      ...(tasksSettings?.markAsPriority && { priority_flag: true }),
      ...getStateAndAssignment({
        markAsCompleted: tasksSettings?.markAsCompleted,
        assignee,
        uid,
        teamId,
      }),
    }),
    [
      uid,
      storeId,
      teamId,
      assignee,
      tasksSettings.markAsCompleted,
      tasksSettings?.markAsPriority,
    ]
  );

  const resetArticleSelectionView = () => {
    dispatch({ type: "RESET_ITEMS" });
    tasksSettingsDispatch({ type: "RESET_SETTINGS" });
    setAssignee({ gid: "", uid: "" });
    setTasksSettingsModalVisible(false);
    setShowAssignModal(false);
  };

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitIsLoading(true);

      if (enableAddonOrdersV2 && addonOrderTaskTypeId) {
        // V2
        const v2Tasks = formatAddonsToCreateV2(selectedItems, {
          taskTypeId: addonOrderTaskTypeId,
          creatorUserId: uid,
          storeId,
          assignedTeamId: assignee.gid || teamId,
          assignedUserId: assignee.uid || null,
          markAsCompleted: tasksSettings?.markAsCompleted,
          markAsPriority: tasksSettings?.markAsPriority,
        });
        await createTasksBatch.mutateAsync(v2Tasks);
        trackerHelper.trackMultiOrderAddonsCreated({
          items,
          addons: v2Tasks,
          refillAreaId,
          storeId,
          markAsCompleted: tasksSettings?.markAsCompleted,
          markAsPriority: tasksSettings?.markAsPriority,
        });
      } else {
        // V1
        const addons = formatAddonsToCreate(
          selectedItems,
          memoizedAddonTemplate
        );
        await createTasksMutation.mutateAsync({ tasks: addons });
        trackerHelper.trackMultiOrderAddonsCreated({
          items,
          addons,
          refillAreaId,
          storeId,
          markAsCompleted: tasksSettings?.markAsCompleted,
          markAsPriority: tasksSettings?.markAsPriority,
        });
      }

      showToast(t("tasksCreatedString"));
    } catch (error) {
      console.error("Error creating multiple add-on tasks", error);
      showToast(t("tasksCreationFailedString"));
    } finally {
      resetArticleSelectionView();
      setSubmitIsLoading(false);
    }
  }, [
    enableAddonOrdersV2,
    addonOrderTaskTypeId,
    memoizedAddonTemplate,
    tasksSettings.markAsCompleted,
    tasksSettings.markAsPriority,
    createTasksMutation,
    createTasksBatch,
    selectedItems,
    items,
    showToast,
    setSubmitIsLoading,
    storeId,
    uid,
    teamId,
    assignee,
    refillAreaId,
    t,
  ]);

  const disableSubmit = useMemo(
    () =>
      !Object.keys(selectedItems).length ||
      submitIsLoading ||
      Object.values(selectedItems).some(
        (article) => article.validation.hasError
      ),
    [selectedItems, submitIsLoading]
  );

  const assignButtonTranslation = useMemo(() => {
    if (tasksSettings.markAsCompleted) {
      return t("completeTasksWithCountString", {
        count: Object.keys(selectedItems).length,
      });
    }
    return t(assignee.uid ? "assignToUserString" : "assignToTeamString");
  }, [assignee.uid, tasksSettings.markAsCompleted, selectedItems, t]);

  const assignButtonDisabled = useMemo(() => {
    if (tasksSettings.markAsCompleted) return false;
    return !assignee.gid || disableSubmit;
  }, [assignee.gid, tasksSettings.markAsCompleted, disableSubmit]);

  const [tasksSettingsModalVisible, setTasksSettingsModalVisible] =
    useState(false);

  const handleAssignModalSubmit = (selectedAssignee: Assignee) => {
    setAssignee({
      gid: selectedAssignee.gid || "",
      uid: selectedAssignee.uid || "",
    });
  };

  return (
    <TasksSettingsContext.Provider
      value={{ tasksSettings, dispatch: tasksSettingsDispatch }}
    >
      <ItemsContext.Provider value={{ items, selectedItems, dispatch }}>
        <FullScreenPopup
          isActionBarHiddenOnScroll={false}
          actionBarContent={
            <>
              <ActionBarContent
                assignButtonText={assignButtonTranslation}
                createButtonText={
                  tasksSettings.markAsCompleted
                    ? "completeMultiOrderAddOnsString"
                    : "createMultiOrderAddOnsString"
                }
                handleSubmit={handleSubmit}
                isLoading={submitIsLoading}
                selectedArticlesCount={Object.keys(selectedItems).length}
                setShowAssignModal={setShowAssignModal}
                disabled={disableSubmit}
                showAssignButton={showAssignModal}
                assignButtonDisabled={assignButtonDisabled}
              />
              <FloatingOptionsButton
                iconOnly
                ssrIcon={EllipsesVerticalPath}
                size="small"
                type="emphasised"
                onClick={() => setTasksSettingsModalVisible(true)}
              />
            </>
          }
          linkbar={<ItemsViewLinkbar refillAreaName={refillAreaName} />}
          noPadding
          fullHeightContent
        >
          <FilterPanel
            filterValues={multiOrderAddonsFilters.filterValues}
            setFilterValues={multiOrderAddonsFilters.setFilterValues}
            useFiltersFactory={multiOrderAddonsFilters.useFiltersFactory}
            feature="media-articles"
          />
          <ItemsListView
            items={items}
            isLoading={isFetching}
            isLoadingMore={isFetchingNextPage}
            hasNextPage={Boolean(hasNextPage)}
            fetchNextPage={fetchNextPage}
            currentPage={data?.pages.length || 0}
            totalPagesCount={data?.pages[0]?.totalPagesCount || 0}
            groupBy={
              multiOrderAddonsFilters.filterValues
                .find((filter) => filter.filterId === "groupBy")
                ?.value?.toString() ?? ""
            }
            clearFilters={() => multiOrderAddonsFilters.setFilterValues([])}
          />
          {showAssignModal && (
            <NewAssign
              onClose={() => setShowAssignModal(false)}
              onSubmit={handleAssignModalSubmit}
              assignees={assignee}
            />
          )}
          <TasksSettingsModal
            show={tasksSettingsModalVisible}
            onClose={() => setTasksSettingsModalVisible(false)}
          />
        </FullScreenPopup>
      </ItemsContext.Provider>
    </TasksSettingsContext.Provider>
  );
};

export default ItemsSelectionView;
